import { environment } from 'environments/environment';
import { Observable, BehaviorSubject, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SummarySendMail } from 'app/core/_base/crud/models/summary-send-mail-model';

@Injectable({
    providedIn: 'root'
})
export class DebitsCreditsService {

    private API_UNPROCESSED_DEBITS_CREDITS_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/listUnprocessed';
    private API_UPDATE_STATUS_CREDIT_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/updateStatusCredit';
    private API_UPDATE_STATUS_DEBIT_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/updateStatusDebit';
    private API_GENERATE_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/generate';
    private API_CONCATENATE_CREDIT_DOCS_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/concatenateCreditDocuments';
    private API_CONCATENATE_DEBIT_DOCS_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/concatenateDebitDocuments';
	private API_CREDIT_DOCS_PDF_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/creditDocumentsPdf';
    private API_DEBIT_DOCS_PDF_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/debitDocumentsPdf';
	private API_SEND_EMAIL_CREDIT_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/sendEmailCredit';
    private API_SEND_EMAIL_DEBIT_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/sendEmailDebit';
    private API_FIND_DEBIT_BY_ID_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/findDebitByID';
    private API_CREDIT_SEARCH_URL = environment.ipserver + 'o2c-backend/private/creditDebitNotes/searchCredit';

    /**
     * Observable process source
     */
    private processUpdate: BehaviorSubject<boolean>;

    /**
     * Observable process stream
     */
    public observerProcessUpdate: Observable<boolean>;

    constructor(
        private httpClient: HttpClient
    ) {
        // init the subject with bolean false
        // cause we need to avoid the first change (value false)
        // and get all the others changes (value true)
        this.processUpdate = new BehaviorSubject<boolean>(false);
        this.observerProcessUpdate = this.processUpdate.asObservable();
    }

    /**
     * Emitter to update the debits and credits list
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-01-4
     */
    public processToUpdateList() {
        this.processUpdate.next(true);
    }

    /**
     * Method responsible to cast the total currency (sopInvoiceTotalCurrency) to number
     * neccessary to sort the tables shown
     * @param api API to use
     *
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2019-12-6
     * @return Observable with a json that contains a list of invoices
     */
    private getMappedDebitsCredits(api: string): Observable<any> {
        return this.httpClient.get(api).pipe(
            map((value: { creditNotes: Array<any>, debitNotes: Array<any> }) => {
                value = this.mapForDebitsAndCredits(value);
                return value;
            })
        );
    }

    /**
     * Method to use while mapping the values of debits and credits
     * @param data data to map
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2019-12-6
     * @return an object with debit and credit notes arrays
     */
    private mapForDebitsAndCredits(data: {
        creditNotes: Array<any>,
        debitNotes: Array<any>
    }): {
        creditNotes: Array<any>,
        debitNotes: Array<any>
    } {
        data.creditNotes.forEach((element: {
            totalCreditNoteValue: any,
            type: string,
            tTransactionNumber: string,
            invoiceHeader: any
        }) => {
            // Formatting the value to remove all the commas from the number
            element.totalCreditNoteValue = Number(element.totalCreditNoteValue.replace(/[^0-9.-]+/g, ''));
            // This map exist because without invoice header we can't show the pdf
            // and also to order the table we need a value different from the transaction number
            element.tTransactionNumber = element.invoiceHeader ? element.invoiceHeader.invoiceNumber : 'INF';
            element.type = 'Credit';
        });
        // mapping the values of debit notes to coincide with the structure of credits to show in the table and modal
        data.debitNotes.forEach((element: {
            creditDate: string,
            transactionDate: string,
            totalCreditNoteValue: number,
            invoiceHeader: any,
            creditNoteNumber: number,
            transactionNumber: number,
            arTranTotalCurrency: string,
            tTransactionNumber: string,
            documentReference: string,
            type: string
        }) => {
            // Formatting the value to remove all the commas from the number
            element.totalCreditNoteValue = Number(element.arTranTotalCurrency.replace(/[^0-9.-]+/g, ''));
            element.creditDate = element.transactionDate;
            element.tTransactionNumber = element.invoiceHeader ? element.invoiceHeader.invoiceNumber : 'INF';
            element.creditNoteNumber = element.transactionNumber;
            element.type = 'Debit';
        });
        return data;
    }

    /**
     * Method that obtains a list of unprocessed invoices by calling invoices rest service
     * @author Castor Transformación Digital - Juan G. Pavas
     * @since 2019-10-31
     * @return Observable with a json that contains a list of invoices
     */
    public getDebitsCreditsUnprocessed(): Observable<any> {
        return this.getMappedDebitsCredits(this.API_UNPROCESSED_DEBITS_CREDITS_URL);
    }

    /**
     * Method to update the status of an invoices list
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2019-12-19
     * @param invoices: list of invoices to update
     * @param newStatus: variable with the new status
     * @return Observable with a text that contains a message of confirmation
     */
    public updateStatusDebitsCredits(debits: any[], credits: any[], newStatus: string): Observable<any> {
        const paramsForPut = new HttpParams().set('newStatus', newStatus);
        const type = 'text' as 'json';
        const creditsRequest = this.httpClient.put(
            this.API_UPDATE_STATUS_CREDIT_URL, credits,
            { params: paramsForPut, responseType: type }
        );
        const debitsRequest = this.httpClient.put(
            this.API_UPDATE_STATUS_DEBIT_URL, debits,
            { params: paramsForPut, responseType: type }
        );
        const sources = [
            credits.length > 0 ? creditsRequest : of(null),
            debits.length > 0 ? debitsRequest : of(null)
        ];
        return forkJoin(sources);
    }

    /**
     * Generates a report with documents concatenated (credit
     * format, revised format and original invoice)
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2019-12-20
     * @param debitsCredits: credit to concatenate in the docs
     * @param creditDoc: boolean to know if it must concatenate this doc type
     * @param revisedDoc: boolean to know if it must concatenate this doc type
     * @param originalInvoiceDoc: boolean to know if it must concatenate this doc type
     * @return Observable with a text that contains a message of confirmation
     */
    public concatenateDocs(
        debitsCredits: any,
        params: {
            creditDoc: boolean, revisedDoc: boolean, originalInvoiceDoc: boolean, isDebit: boolean
        }): Observable<any> {
        // verify if is a debit, else should be a credit
        let debitCreditDoc = 'creditDoc';
        if (params.isDebit) {
            debitCreditDoc = 'debitDoc';
        }
        const url = params.isDebit ? this.API_CONCATENATE_DEBIT_DOCS_URL : this.API_CONCATENATE_CREDIT_DOCS_URL;
        const paramsForPut = new HttpParams()
            .set('revisedDoc', String(params.revisedDoc))
            .set(debitCreditDoc, String(params.creditDoc))
            .set('originalInvoiceDoc', String(params.originalInvoiceDoc));
        const type = 'text' as 'json';
        return this.httpClient.post(url, debitsCredits, { params: paramsForPut, responseType: type });
    }
	 /**
     * Generates a report with documents concatenated (credit
     * format, revised format and original invoice)
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2019-12-20
     * @param debitsCredits: credit to concatenate in the docs
     * @param creditDoc: boolean to know if it must concatenate this doc type
     * @param revisedDoc: boolean to know if it must concatenate this doc type
     * @param originalInvoiceDoc: boolean to know if it must concatenate this doc type
     * @return Observable with a text that contains a message of confirmation
     */
	  public generateDebitOrCreditPDF(
        debitsCredits: any,
        params: {
            creditDoc: boolean, revisedDoc: boolean, originalInvoiceDoc: boolean, isDebit: boolean
        }): Observable<any> {
        // verify if is a debit, else should be a credit
        let debitCreditDoc = 'creditDoc';
        if (params.isDebit) {
            debitCreditDoc = 'debitDoc';
        }
        const url = params.isDebit ? this.API_DEBIT_DOCS_PDF_URL : this.API_CREDIT_DOCS_PDF_URL;
        const paramsForPut = new HttpParams()
            .set('revisedDoc', String(params.revisedDoc))
            .set(debitCreditDoc, String(params.creditDoc))
            .set('originalInvoiceDoc', String(params.originalInvoiceDoc));
        const type = 'text' as 'json';
        return this.httpClient.post(url, debitsCredits, { params: paramsForPut, responseType: type });
    }

    /**
     * Generates a report with credits registered in DB
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2019-12-23
     * @param debitsCredits: credit to generate the doc
     * @param toLog: status to know if it must be written in the log
     * @return Observable with a text that contains a message of confirmation
     */
    public generateDebitCredit(debitsCredits: any, toLog: boolean, isCustomerCreditFormat: boolean, isEditableCredit: boolean = false): Observable<any> {
        const paramsForPut = new HttpParams()
            .set('toLog', String(toLog))
            .set('isEditableCredit', String(isEditableCredit))
            .set('isCustomerCreditFormat', String(isCustomerCreditFormat));
        const type = 'text' as 'json';
        return this.httpClient.post(this.API_GENERATE_URL, debitsCredits, { params: paramsForPut, responseType: type });
    }

    /**
     * Makes an object to send with the generateDebitCredit method
     * @param debitCredit data to pack
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2020-01-2
     */
    public objectForGenerate(debitCredit: any, creditOrDebit?: string): { creditNotes: any[], debitNotes: any[] } {
        let type = debitCredit.type;
        if (creditOrDebit) {
            type = creditOrDebit;
        }
        return type === 'Credit' ?
            { creditNotes: [debitCredit], debitNotes: null } : { creditNotes: null, debitNotes: [debitCredit] };
    }

    /**
     * managing the send email request (in batch) and
     * redirecting to the appropriate service
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2019-12-24
     * @param debitsCredits: list of invoices to update
     * @param originalDoc: flag to verify if the original document should be generated
     * @param revisedDoc: flag to verify if the revised document should be generated
     * @param creditDoc: flag to verify if the credit document should be generated
     * @param newRecipients: List of new recipients of the email (manual process)
     * @return Observable with a text that contains a message of confirmation
     */
    public sendEmail(
        debitsCredits: any,
        params: {
            originalDoc: boolean,
            revisedDoc: boolean,
            creditDoc: boolean,
            newRecipients: string,
            isDebit: boolean,
			isResend:boolean
        }
    ): Observable<SummarySendMail[]> {
        // verify if is a debit, else should be a credit
        let debitCreditDoc = 'creditDoc';
        if (params.isDebit) {
            debitCreditDoc = 'debitDoc';
        }
        const url = params.isDebit ? this.API_SEND_EMAIL_DEBIT_URL : this.API_SEND_EMAIL_CREDIT_URL;
        const paramsForPut = new HttpParams()
            .set('originalDoc', String(params.originalDoc))
            .set('revisedDoc', String(params.revisedDoc))
            .set('newRecipients', params.newRecipients)
			.set('isResend', String(params.isResend))
            .set(debitCreditDoc, String(params.creditDoc));
        const type = 'text' as 'json';
        return this.httpClient.post<any>(url, debitsCredits, { params: paramsForPut, responseType: type });
    }

    /**
     * Return the object with invoice header necessary for debit docs request
     * @param gemDBKey number of the invoice in the db
     * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
     * @since 2019-01-3
     */
    public findDebitByID(gemDBKey: number) {
        const paramsForReq = new HttpParams()
            .set('gemDBKey', String(gemDBKey));
        return this.httpClient.get(this.API_FIND_DEBIT_BY_ID_URL, { params: paramsForReq });
    }

    public creditSearch(filters: { division: string; invoiceNumber: string; orderNumber: string; creditNumber: string; clogNumber: string; complainNumber: string;}): Observable<any> {
        const paramsForReq = new HttpParams()
            .set('division', filters.division)
            .set('invoiceNumber', filters.invoiceNumber)
            .set('orderNumber', filters.orderNumber)
			.set('complainNumber', filters.complainNumber)
			.set('clogNumber', filters.clogNumber)
            .set('creditNumber', filters.creditNumber);
        return this.httpClient.get(this.API_CREDIT_SEARCH_URL, { params: paramsForReq });
    }
}
